import { useState } from "react";

import PageContainer from "./containers/page-container";
import VideoListItem from "./components/videos/video-list-item";
import VideoListSkeletonLoader from "./components/videos/video-list-skeleton-loader";
import VideoPlayer from "./components/videos/video-player";
import SearchBar from "./components/shared/search-bar";
import IDENTIFIERS from "@/constants/test_id";
import { useVideoList } from "@/hooks/use-videos";
import { getErrorMessage } from "@/lib/error-message-utils";
import type { Video } from "@/model/videos";
import { useActionLogger } from "@/services/logger/logger-hooks";

const VideoList = () => {
  const { data: videoList, isLoading, isError, error } = useVideoList();
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);
  const [searchText, setSearchText] = useState("");
  const { logAction } = useActionLogger();

  // Logs every video the member opens, from the grid or "Other videos"
  const selectVideo = (video: Video) => {
    logAction(IDENTIFIERS.VIDEOS.VIDEO_ITEM, {
      video: {
        topic: video.topic,
        source: video.source,
        type: video.type,
        link: video.link,
      },
    });
    setSelectedVideo(video);
  };

  if (isLoading) return <VideoListSkeletonLoader />;

  if (isError) {
    return (
      <p className="p-8 text-center text-destructive">
        {getErrorMessage(error)}
      </p>
    );
  }

  if (!videoList?.length) {
    return (
      <p className="p-8 text-center text-muted-foreground">
        No videos available
      </p>
    );
  }

  if (selectedVideo) {
    return (
      <VideoPlayer
        video={selectedVideo}
        videoList={videoList}
        onSelectVideo={selectVideo}
        onClose={() => setSelectedVideo(null)}
      />
    );
  }

  const normalizedSearch = searchText.trim().toLowerCase();
  const filteredVideos = videoList.filter((video) =>
    video.topic.toLowerCase().includes(normalizedSearch),
  );

  return (
    <div className="px-4 py-8">
      <SearchBar
        value={searchText}
        onChange={setSearchText}
        ariaLabel="Search videos"
        className="mb-6"
      />

      {filteredVideos.length ? (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filteredVideos.map((video, index) => (
            // Links aren't guaranteed unique, so the index is part of the key
            <VideoListItem
              key={`${video.link}-${index}`}
              video={video}
              onSelect={() => selectVideo(video)}
              testId={`${IDENTIFIERS.VIDEOS.VIDEO_ITEM}-${index}`}
            />
          ))}
        </div>
      ) : (
        <p className="p-8 text-center text-muted-foreground">
          No videos match &quot;{searchText.trim()}&quot;
        </p>
      )}
    </div>
  );
};

const Videos = () => (
  <PageContainer title="Videos">
    <VideoList />
  </PageContainer>
);

export default Videos;
